import React from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import { Close, Warning } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { useQuery } from "@tanstack/react-query";

import {
  Plan,
  PlanTier,
  billingPlanPrice,
  tierIndex,
  planClients,
  planStatus,
} from "../../constants";
import BillingsService from "../../services/BillingsService";
import {
  humanizeUserPlan,
  isPlanYearly,
  monthlyBillingPlan,
} from "../../utils/user";
import dayjs from "dayjs";
import {
  AddonCode,
  InvoiceLineItemDto,
  PeriodDto,
  AddonDto,
  AddonUpdateDto,
} from "@growth-machine-llc/stridist-api-client";
import { formatCurrency } from "../../utils/currency";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useAddons } from "../../hooks/useAddons";
import LoadingButton from "../button/LoadingButton";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(3, 4, 2, 4),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dialogTitleText: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.common.black,
  },
  closeButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
    },
  },
  modalContent: {
    padding: theme.spacing(0, 4, 3, 4),
  },
  invoiceModalActions: {
    justifyContent: "flex-end",
    padding: theme.spacing(4),
    gap: theme.spacing(1),
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  previewPlanName: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.palette.common.black,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  previewAddonName: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.common.black,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  previewPlanSub: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  previewAmount: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.common.black,
  },
  confirmAmount: {
    fontSize: 18,
    color: theme.palette.common.black,
  },
  previewSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  previewSectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.common.black,
  },
  previewNextDate: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  detailsToggle: {
    textDecoration: "none",
    textTransform: "none",
    fontWeight: 600,
    minWidth: "auto",
    padding: 0,
    color: theme.palette.primary.main,
    fontSize: 14,
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  detailsToggleRow: {
    marginTop: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
  },
  sectionBlock: {
    marginTop: theme.spacing(2),
  },
  dividerSpacing: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  lineItems: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.radius.md,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  lineItemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
  },
  lineItemName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.common.black,
  },
  lineItemMeta: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  lineItemAmount: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.common.black,
  },
  lineItemMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  badge: {
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.shape.radius.sm,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    fontSize: 12,
    fontWeight: 600,
  },
  credit: {
    color: theme.palette.success.dark,
  },
  removedAddon: {
    position: "relative",
    opacity: 0.6,
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      top: "50%",
      height: 1,
      backgroundColor: theme.palette.text.primary,
      transform: "translateY(-50%)",
    },
  },
  addonQuantity: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  actionButton: {
    padding: theme.spacing(1, 3),
    height: 43,
  },
  warningAlert: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    fontSize: 13,
    "& .MuiAlert-icon": {
      fontSize: 18,
    },
  },
}));

export interface InvoiceBreakdownDialogProps {
  plan?: Plan;
  addons?: Array<AddonUpdateDto>;
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const InvoiceBreakdownDialog = ({
  plan,
  addons,
  open,
  loading,
  onClose,
  onConfirm,
}: InvoiceBreakdownDialogProps) => {
  const s = useStyles();
  const [showRecurringDetails, setShowRecurringDetails] = React.useState(false);
  const [showDueTodayDetails, setShowDueTodayDetails] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setShowRecurringDetails(false);
      setShowDueTodayDetails(false);
    }
  }, [open, plan]);

  const { plan: currentPlan } = useCurrentUser();
  const { data: currentAddons } = useAddons();

  const { subscription, isImpersonating } = useCurrentUser();
  const isTrialing = subscription?.status === planStatus.TRIALING;

  const { data: invoicePreview, isFetching: loadingInvoicePreview } = useQuery({
    queryKey: ["invoice-preview", plan, addons],
    queryFn: () =>
      BillingsService.getInvoiceBreakdown({
        plan,
        addons: addons?.map((addon) => ({
          addonCode: addon.addonCode,
          quantity: addon.quantity,
        })),
      }),
    enabled: open && Boolean(plan || (addons && addons.length > 0)),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
  const currency = invoicePreview?.currency ?? "USD";

  // Determine plan change type

  const effectivePlan = plan || (currentPlan as Plan);

  const recurringAmount =
    invoicePreview?.nextBillingTotal ??
    (billingPlanPrice[effectivePlan]
      ? billingPlanPrice[effectivePlan][tierIndex[PlanTier.DEFAULT]]
      : 0);

  const amountDueToday = invoicePreview?.amountDueToday ?? 0;

  const nextBillingDate = invoicePreview?.nextBillingDate;
  const formattedNextDate = nextBillingDate
    ? nextBillingDate.format("MMMM DD, YYYY")
    : null;

  const recurringLineItems = invoicePreview?.nextCycleLineItems ?? [];
  const dueTodayLineItems = invoicePreview?.dueTodayLineItems ?? [];

  const monthlyPlan = monthlyBillingPlan(effectivePlan);
  const monthlyEquivalentPrice =
    billingPlanPrice[monthlyPlan]?.[tierIndex[PlanTier.DEFAULT]] ?? 0;

  const formatInterval = (interval?: string, count?: number) => {
    if (!interval) return null;
    const safeCount = count ?? 1;
    if (safeCount === 1) {
      return `/${interval}`;
    }
    return `every ${safeCount} ${interval}${safeCount > 1 ? "s" : ""}`;
  };

  const formatPeriodRange = (period?: PeriodDto) => {
    if (!period?.start || !period?.end) return null;
    return `${period.start.format("MMM D, YYYY")} - ${period.end.format("MMM D, YYYY")}`;
  };

  const renderLineItem = (item: InvoiceLineItemDto, index: number) => {
    const displayAmount = item?.amount ?? 0;
    const isCredit = displayAmount < 0 || item?.isCredit;
    const recurringLabel =
      item?.type === "recurring"
        ? formatInterval(item?.interval, item?.intervalCount)
        : null;
    const periodRange = formatPeriodRange(item?.period);

    return (
      <Box key={`${item?.productName}-${index}`} className={s.lineItemRow}>
        <Box>
          <Typography className={s.lineItemName}>
            {item?.description ?? item?.productName ?? "Line item"}
          </Typography>
          {(item?.quantity ||
            recurringLabel ||
            item?.isProration ||
            isCredit ||
            periodRange) && (
            <Box className={s.lineItemMetaRow}>
              {item?.quantity ? (
                <Typography className={s.lineItemMeta}>
                  Qty {item.quantity}
                </Typography>
              ) : null}
              {recurringLabel ? (
                <Typography className={s.lineItemMeta}>
                  {recurringLabel}
                </Typography>
              ) : null}
              {item?.isProration ? (
                <Typography className={s.badge}>Proration</Typography>
              ) : null}
              {isCredit ? (
                <Typography className={s.badge}>Credit</Typography>
              ) : null}
              {periodRange ? (
                <Typography className={s.lineItemMeta}>
                  {periodRange}
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
        <Typography
          className={`${s.lineItemAmount} ${isCredit ? s.credit : ""}`}
        >
          {formatCurrency(displayAmount, currency, "long")}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className={s.dialogTitle}>
        <Typography component="span" className={s.dialogTitleText}>
          Confirm your updates
        </Typography>
        <IconButton
          className={s.closeButton}
          onClick={onClose}
          size="small"
          disableRipple
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={s.modalContent}>
        {/* Recurring Payment Block */}
        <Box className={s.sectionBlock}>
          <Box className={s.previewSection}>
            <Box>
              <Typography className={s.previewSectionTitle}>
                What you'll pay starting
              </Typography>
              {formattedNextDate && (
                <Typography className={s.previewNextDate}>
                  {formattedNextDate}
                </Typography>
              )}
            </Box>
            <Typography className={s.previewAmount}>
              {formatCurrency(recurringAmount, currency, "long")}
            </Typography>
          </Box>
          <Box className={s.detailsToggleRow}>
            <Button
              disableRipple
              className={s.detailsToggle}
              size="small"
              variant="text"
              onClick={() => setShowRecurringDetails((prev) => !prev)}
              disabled={loadingInvoicePreview || !recurringLineItems.length}
            >
              {showRecurringDetails ? "Hide details" : "View details"}
            </Button>
          </Box>
          <Collapse in={showRecurringDetails}>
            <Box className={s.lineItems}>
              {(recurringLineItems.length ? recurringLineItems : [])?.map(
                (item: InvoiceLineItemDto, index: number) =>
                  renderLineItem(item, index),
              )}
              {!recurringLineItems.length && !loadingInvoicePreview && (
                <Typography className={s.lineItemMeta}>
                  Preview not available yet.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>

        <Divider className={s.dividerSpacing} />

        {/* Amount Due Today Block */}
        <Box className={s.sectionBlock}>
          <Box className={s.previewSection}>
            <Typography className={s.previewSectionTitle}>
              Amount due today
            </Typography>
            <Typography className={s.previewAmount}>
              {formatCurrency(amountDueToday, currency, "long")}
            </Typography>
          </Box>
          <Box className={s.detailsToggleRow}>
            <Button
              className={s.detailsToggle}
              size="small"
              variant="text"
              onClick={() => setShowDueTodayDetails((prev) => !prev)}
              disabled={loadingInvoicePreview || !dueTodayLineItems.length}
            >
              {showDueTodayDetails ? "Hide details" : "View details"}
            </Button>
          </Box>
          <Collapse in={showDueTodayDetails}>
            <Box className={s.lineItems}>
              {(dueTodayLineItems.length ? dueTodayLineItems : [])?.map(
                (item: InvoiceLineItemDto, index: number) =>
                  renderLineItem(item, index),
              )}
              {!dueTodayLineItems.length && !loadingInvoicePreview && (
                <Typography className={s.lineItemMeta}>
                  Nothing due today for this change.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
        {/* Trial End Warning */}
        {isTrialing ? (
          <Alert
            severity="warning"
            icon={<Warning fontSize="small" />}
            className={s.warningAlert}
          >
            <Typography variant="body2" component="span">
              Your trial will end immediately when you update your plan.
            </Typography>
          </Alert>
        ) : null}
        {isImpersonating ? (
          <Alert
            severity="warning"
            icon={<Warning fontSize="small" />}
            className={s.warningAlert}
          >
            <Typography variant="body2" component="span">
              This will affect the plan of the user you are currently
              impersonating.
            </Typography>
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions className={s.invoiceModalActions}>
        <Button variant="outlined" onClick={onClose} className={s.actionButton}>
          Back
        </Button>
        <LoadingButton
          className={s.actionButton}
          variant="contained"
          onClick={onConfirm}
          disabled={loadingInvoicePreview}
          loading={loading}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceBreakdownDialog;
